import React, { useEffect, useRef } from 'react';

/**
 * TradingView Advanced Chart widget styled to match xceller's dark glass UI.
 * The widget is a sandboxed iframe served from s3.tradingview.com — we feed it
 * a JSON config via a <script> tag and TradingView renders the chart inside.
 *
 * Customizable: symbol, interval, theme tokens from the parent ThemeProvider.
 * Defaults to gold (OANDA:XAUUSD) since that's what xceller signals trade.
 */
export default function TradingViewChart({
  symbol = 'OANDA:XAUUSD',
  interval = '15',
  theme = 'dark',
  height = 320,
  t,
  isDark = true,
}) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    // Wipe any previous widget if the symbol or theme changed.
    containerRef.current.innerHTML = '';

    const widgetWrap = document.createElement('div');
    widgetWrap.className = 'tradingview-widget-container__widget';
    widgetWrap.style.height = '100%';
    widgetWrap.style.width = '100%';
    containerRef.current.appendChild(widgetWrap);

    // xceller accent palette — fed into TradingView's `overrides`.
    const positive = '#10b981';
    const negative = '#ef4444';
    const gridLine = isDark ? 'rgba(148,163,184,0.08)' : 'rgba(15,23,42,0.06)';
    // True transparency. TradingView honors rgba alpha=0 on the pane background.
    const transparent = 'rgba(0,0,0,0)';
    const textColor = isDark ? '#cbd5e1' : '#0f172a';

    const config = {
      autosize: true,
      symbol,
      interval,
      timezone: 'Etc/UTC',
      theme,
      style: '1', // candles
      locale: 'en',
      backgroundColor: transparent,
      gridColor: gridLine,
      hide_top_toolbar: false,
      hide_side_toolbar: true,    // hides the left drawing-tool rail to shrink width
      hide_legend: false,
      hide_volume: true,          // volume sub-pane was eating ~30% of vertical space
      allow_symbol_change: true,
      save_image: false,
      enable_publishing: false,
      withdateranges: false,      // small bar at the bottom; we hide it for compactness
      details: false,
      // Do NOT inject extra studies — the widget already has Volume baked in;
      // adding it again creates the duplicate volume pane the user noticed.
      overrides: {
        'paneProperties.background': transparent,
        'paneProperties.backgroundType': 'solid',
        'paneProperties.backgroundGradientStartColor': transparent,
        'paneProperties.backgroundGradientEndColor': transparent,
        'paneProperties.vertGridProperties.color': gridLine,
        'paneProperties.horzGridProperties.color': gridLine,
        'paneProperties.crossHairProperties.color': isDark ? '#94a3b8' : '#475569',
        'scalesProperties.textColor': textColor,
        'scalesProperties.lineColor': gridLine,
        'scalesProperties.backgroundColor': transparent,
        'mainSeriesProperties.candleStyle.upColor': positive,
        'mainSeriesProperties.candleStyle.downColor': negative,
        'mainSeriesProperties.candleStyle.borderUpColor': positive,
        'mainSeriesProperties.candleStyle.borderDownColor': negative,
        'mainSeriesProperties.candleStyle.wickUpColor': positive,
        'mainSeriesProperties.candleStyle.wickDownColor': negative,
        'mainSeriesProperties.hollowCandleStyle.upColor': positive,
        'mainSeriesProperties.hollowCandleStyle.downColor': negative,
      },
    };

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
    script.async = true;
    script.innerHTML = JSON.stringify(config);
    containerRef.current.appendChild(script);

    return () => {
      if (containerRef.current) containerRef.current.innerHTML = '';
    };
  }, [symbol, interval, theme, isDark]);

  return (
    <div style={{
      position: 'relative',
      height,
      width: '100%',
      borderRadius: 12,
      overflow: 'hidden',
      // Soft inner border so the chart visually merges into our glass cards.
      boxShadow: t ? `inset 0 0 0 1px ${t.cardBorder}` : 'none',
    }}>
      <div ref={containerRef} className="tradingview-widget-container" style={{ height: '100%', width: '100%' }} />
      {/* TradingView requires a small attribution link per their free-license terms. */}
      <div className="tradingview-widget-copyright" style={{
        position: 'absolute', bottom: 4, right: 8, fontSize: 9, opacity: 0.4, pointerEvents: 'none',
      }}>
        <span>Chart by TradingView</span>
      </div>
    </div>
  );
}
